import { Wallet, Building2, TrendingUp, TrendingDown, ArrowLeftRight } from 'lucide-react';
import type { WiseBalance } from '@/lib/wiseService';
import type { MercuryAccount, MercuryTransaction } from '@/lib/mercuryService';

interface AccountsReportSectionProps {
  wiseBalances: WiseBalance[];
  mercuryAccounts: MercuryAccount[];
  mercuryTransactions: MercuryTransaction[];
  isAllTime: boolean;
  monthLabel: string;
}

function fmt(value: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function getFlagEmoji(currency: string): string {
  const currencyToCountry: Record<string, string> = {
    USD: 'US', EUR: 'EU', GBP: 'GB', MVR: 'MV', AUD: 'AU', CAD: 'CA',
    JPY: 'JP', CHF: 'CH', SGD: 'SG', NZD: 'NZ', INR: 'IN', AED: 'AE',
    HKD: 'HK', SEK: 'SE', NOK: 'NO', DKK: 'DK', PLN: 'PL', CZK: 'CZ',
    HUF: 'HU', RON: 'RO', BGN: 'BG', HRK: 'HR', TRY: 'TR', ZAR: 'ZA',
    BRL: 'BR', MXN: 'MX', IDR: 'ID', MYR: 'MY', PHP: 'PH', THB: 'TH',
    KRW: 'KR', TWD: 'TW', CNY: 'CN',
  };
  const code = currencyToCountry[currency];
  if (!code) return '';
  if (code === 'EU') return '\u{1F1EA}\u{1F1FA}';
  return String.fromCodePoint(
    ...code.split('').map(c => 0x1f1e6 + c.charCodeAt(0) - 65)
  );
}

export default function AccountsReportSection({
  wiseBalances,
  mercuryAccounts,
  mercuryTransactions,
  isAllTime,
  monthLabel,
}: AccountsReportSectionProps) {
  const hasWise = wiseBalances.length > 0;
  const hasMercury = mercuryAccounts.length > 0;

  if (!hasWise && !hasMercury) return null;

  const totalWiseUsd = wiseBalances.reduce(
    (sum, b) => (b.currency === 'USD' ? sum + b.amount.value : sum),
    0
  );

  const totalMercuryBalance = mercuryAccounts.reduce(
    (sum, a) => sum + a.currentBalance,
    0
  );

  const mercuryIn = mercuryTransactions
    .filter(t => t.amount > 0)
    .reduce((s, t) => s + t.amount, 0);

  const mercuryOut = mercuryTransactions
    .filter(t => t.amount < 0)
    .reduce((s, t) => s + Math.abs(t.amount), 0);

  const grandTotal = totalWiseUsd + totalMercuryBalance;

  const periodLabel = isAllTime ? 'All Time' : monthLabel;

  return (
    <div className="glass-panel rounded-2xl p-5">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2.5">
          <div className="p-1.5 rounded-xl bg-sky-500/10">
            <Building2 size={16} className="text-sky-600" />
          </div>
          <h2 className="text-sm font-bold text-white">Account Balances</h2>
        </div>
        <div className="text-right">
          <div className="text-[11px] text-white">Combined USD</div>
          <div className="text-base font-bold text-white">{fmt(grandTotal)}</div>
        </div>
      </div>
      <p className="text-[11px] text-white mb-5 ml-9">
        {isAllTime
          ? 'Current balances across all connected accounts.'
          : `Account balances and activity for ${periodLabel}.`}
      </p>

      <div className="space-y-4">
        {hasWise && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Wallet size={14} className="text-teal-600" />
              <span className="text-xs font-bold text-white">Wise Accounts</span>
              <span className="text-[10px] text-white ml-auto">
                {wiseBalances.length} {wiseBalances.length === 1 ? 'currency' : 'currencies'}
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {wiseBalances.map(b => (
                <div
                  key={b.id}
                  className="flex items-center justify-between p-3 glass-inner rounded-xl"
                >
                  <div className="flex items-center gap-2.5">
                    <span className="text-base">{getFlagEmoji(b.currency)}</span>
                    <div>
                      <p className="text-xs font-semibold text-white">{b.currency}</p>
                      <p className="text-[10px] text-white">{b.name || 'Balance'}</p>
                    </div>
                  </div>
                  <span className="text-xs font-bold text-white tabular-nums">
                    {fmt(b.amount.value, b.currency)}
                  </span>
                </div>
              ))}
            </div>
            {totalWiseUsd > 0 && (
              <div className="flex items-center justify-between mt-2 px-3 py-2 bg-teal-500/10 rounded-xl">
                <span className="text-[11px] font-medium text-teal-700">USD Total</span>
                <span className="text-xs font-bold text-teal-700 tabular-nums">{fmt(totalWiseUsd)}</span>
              </div>
            )}
          </div>
        )}

        {hasMercury && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Building2 size={14} className="text-sky-600" />
              <span className="text-xs font-bold text-white">Mercury Accounts</span>
              <span className="text-[10px] text-white ml-auto">
                {mercuryAccounts.length} {mercuryAccounts.length === 1 ? 'account' : 'accounts'}
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {mercuryAccounts.map(a => (
                <div
                  key={a.id}
                  className="flex items-center justify-between p-3 glass-inner rounded-xl"
                >
                  <div className="flex items-center gap-2.5">
                    <div className="w-7 h-7 rounded-lg bg-sky-100 flex items-center justify-center">
                      <Building2 size={12} className="text-sky-600" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-white">{a.name}</p>
                      <p className="text-[10px] text-white capitalize">{a.kind}</p>
                    </div>
                  </div>
                  <span className="text-xs font-bold text-white tabular-nums">
                    {fmt(a.currentBalance)}
                  </span>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between mt-2 px-3 py-2 bg-sky-500/10 rounded-xl">
              <span className="text-[11px] font-medium text-sky-700">Total Balance</span>
              <span className="text-xs font-bold text-sky-700 tabular-nums">{fmt(totalMercuryBalance)}</span>
            </div>

            {mercuryTransactions.length > 0 && (
              <div className="grid grid-cols-2 gap-2 mt-2">
                <div className="flex items-center gap-2 p-2.5 bg-emerald-500/10 rounded-xl">
                  <TrendingUp size={13} className="text-emerald-800" />
                  <div>
                    <p className="text-[10px] text-emerald-700 font-medium">
                      {isAllTime ? 'Total In' : 'Monthly In'}
                    </p>
                    <p className="text-xs font-bold text-emerald-700 tabular-nums">{fmt(mercuryIn)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 p-2.5 bg-red-500/10 rounded-xl">
                  <TrendingDown size={13} className="text-red-600" />
                  <div>
                    <p className="text-[10px] text-red-700 font-medium">
                      {isAllTime ? 'Total Out' : 'Monthly Out'}
                    </p>
                    <p className="text-xs font-bold text-red-700 tabular-nums">{fmt(mercuryOut)}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {hasWise && hasMercury && (
          <div className="flex items-center justify-between px-4 py-3 bg-white/10 rounded-xl border border-white/10">
            <div className="flex items-center gap-2">
              <ArrowLeftRight size={14} className="text-white" />
              <span className="text-xs font-bold text-white">Grand Total (USD)</span>
            </div>
            <span className="text-sm font-bold text-white tabular-nums">{fmt(grandTotal)}</span>
          </div>
        )}
      </div>
    </div>
  );
}
